import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

import { useBizyExternalOpenStore } from '../store/use-bizy-external-open-store';

interface UseBizyExternalOpenAutoSendOptions {
	isPanelOpen: boolean;
	isEmptyState: boolean;
	sendMessage: (message: string) => void;
	resetChat: () => void;
	shouldScrollRef: MutableRefObject<boolean>;
}

/**
 * Auto-sends a starter message when the panel was opened externally
 * (via a bizy:open event, e.g. "Request a feature" button).
 *
 * Three scenarios:
 * - Empty chat + starterMessage: sends immediately, clears pending request
 * - Existing conversation + starterMessage: shows a confirmation dialog
 *   asking the user whether to start a new conversation or keep the current one
 * - No starterMessage (promptHint only): does nothing, pending request stays
 *   for the chat view to use as input placeholder
 *
 * Returns the current pendingRequest plus dialog state for the chat view.
 */
export function useBizyExternalOpenAutoSend({ isPanelOpen, isEmptyState, sendMessage, resetChat, shouldScrollRef }: UseBizyExternalOpenAutoSendOptions) {
	const pendingRequest = useBizyExternalOpenStore((state) => state.pendingRequest);
	const clearPendingRequest = useBizyExternalOpenStore((state) => state.clearPendingRequest);
	const handledRef = useRef(false);
	const [showConflictDialog, setShowConflictDialog] = useState(false);
	const [pendingSend, setPendingSend] = useState<string | null>(null);

	useEffect(() => {
		if (!isPanelOpen || !pendingRequest) return;

		if (isEmptyState && pendingRequest.starterMessage && !handledRef.current) {
			handledRef.current = true;
			sendMessage(pendingRequest.starterMessage);
			shouldScrollRef.current = true;
			clearPendingRequest();
		} else if (!isEmptyState && pendingRequest.starterMessage) {
			setShowConflictDialog(true);
		}
	}, [isPanelOpen, isEmptyState, pendingRequest, sendMessage, clearPendingRequest, shouldScrollRef]);

	useEffect(() => {
		if (!pendingRequest) {
			handledRef.current = false;
		}
	}, [pendingRequest]);

	// Send deferred message once resetChat has flushed and the chat is empty
	useEffect(() => {
		if (pendingSend && isEmptyState) {
			sendMessage(pendingSend);
			shouldScrollRef.current = true;
			clearPendingRequest();
			setPendingSend(null);
		}
	}, [pendingSend, isEmptyState, sendMessage, shouldScrollRef, clearPendingRequest]);

	const handleConfirmNewConversation = useCallback(() => {
		const message = pendingRequest?.starterMessage;
		if (!message) return;
		resetChat();
		setShowConflictDialog(false);
		handledRef.current = true;
		setPendingSend(message);
	}, [pendingRequest, resetChat]);

	const handleKeepCurrentConversation = useCallback(() => {
		setShowConflictDialog(false);
		clearPendingRequest();
	}, [clearPendingRequest]);

	return {
		pendingRequest,
		showConflictDialog,
		handleConfirmNewConversation,
		handleKeepCurrentConversation,
	};
}
